import React, { FC, useState } from 'react';

import { Box, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  ChakraMarkdown,
  CustomTooltip,
  formatNumberWithComma,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { PoliciesUnderSubControlDrawer } from 'containers/Compliance/Components/ComplianceTables/ComplianceControlTable/PoliciesUnderSubControlDrawer';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';

import { renderPassedChecks } from '../utils';

interface Props {
  control: Record<string, any>;
  rowIndex: number;
  isExpanded: boolean;
}

export const SubControlTable: FC<Props> = props => {
  const { control, rowIndex, isExpanded } = props;
  const {
    subControlStandardRules,
    selectedBlueprint,
    environment,
  } = useSelector(selectCompliance);

  const { renderRulesActions, rulesRowIndex } = useCompliance();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});

  const dispatch = useDispatch();
  const [summary, setSummary] = useState<{
    data: Record<string, any>[];
    total: number;
  }>({ data: [], total: 0 });
  const subControl = useDisclosure();

  useDeepCompareEffect(() => {
    isExpanded &&
      dispatch(
        actions.getSubControlStandardRules({
          q: {
            blueprintId: selectedBlueprint?.id,
            level: 'sub_control',
            control: control?.control,
            environmentId: environment?.id,
          },
          onSuccess: res => {
            setSummary({ data: res?.items || [], total: res?.total || 0 });
          },
        }),
      );
  }, [dispatch, control, environment]);

  const columns = [
    {
      header: <Box pl={10}>Sub-Control</Box>,
      accessor: 'sub_control',
      align: 'left',
      render: ({ value }) => {
        return (
          <WithResourceIcon resourceType={selectedBlueprint?.compliance!}>
            <Box>{value}</Box>
          </WithResourceIcon>
        );
      },
      styles: { width: '150px', cell: { maxWidth: '150px' } },
    },
    {
      header: 'Sub-Control Description',
      accessor: 'sub_control_description',
      align: 'left',
      render: ({ value }) => (
        <CustomTooltip
          label={<ChakraMarkdown content={value || '-'} />}
          styles={{ text: { w: 'full' } }}
        >
          <Box whiteSpace="wrap" noOfLines={2} flex={1}>
            {value || '-'}
          </Box>
        </CustomTooltip>
      ),
    },
    {
      header: 'Ariksa Policies',
      accessor: 'rules',
      render: ({ value }) => value.total || 0,
      styles: { width: '130px', cell: { maxWidth: '130px' } },
    },
    {
      header: 'Entities',
      accessor: 'check_stats',
      render: ({ value }) => formatNumberWithComma(value?.total_entities || 0),
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'Exceptions',
      accessor: 'rules',
      render: ({ value }) => {
        if (value?.total && value?.selected) {
          return value?.total - value?.selected;
        } else {
          return 0;
        }
      },
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'Passed Policy Checks',
      accessor: 'check_stats',
      render: renderPassedChecks,
      styles: { width: '220px', cell: { maxWidth: '220px' } },
      align: 'left',
    },
    {
      header: 'Actions',
      render: renderRulesActions,
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
  ];

  return (
    <>
      <Box w="full" h="full" flex={1}>
        <Table
          data={summary?.data}
          columns={columns}
          cursor="pointer"
          isLoading={
            subControlStandardRules.isLoading && rowIndex === rulesRowIndex
          }
          onRowClick={row => {
            setCurrentRecord(row);
            subControl.onOpen();
          }}
        />
      </Box>
      {subControl.isOpen && (
        <PoliciesUnderSubControlDrawer
          subControl={currentRecord}
          isOpen={subControl.isOpen}
          onClose={subControl.onClose}
        />
      )}
    </>
  );
};
